import React from 'react';
import { css } from '@emotion/css';

import { useSearchParams } from 'react-router-dom';
import SchufaCheckJpg from './schufa-check.jpg';
import SchufaCheckWebp from './schufa-check.webp';
import SchufaCheckJpgMobile from './schufa-check-mobile.jpg';
import SchufaCheckWebpMobile from './schufa-check-mobile.webp';
import PaperAndPenPng from './paper-and-pen.png';
import PaperAndPenWebp from './paper-and-pen.webp';
import LetterPng from './letter.png';
import LetterWebp from './letter.webp';
import DownloadPng from './download.png';
import DownloadWebp from './download.webp';
import CoinPng from './coin.png';
import CoinWebp from './coin.webp';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { APP_CONFIG } from '../../../../../config';
import { ContractSource } from '../../../../../@types/membership';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { FAQList } from '../../../../common/display/faqTab/faqList/FAQList';
import { CustomerService } from '../../../../common/display/faqTab/customerService/CustomerService';
import { GRAY300, TEAL200 } from '../../../../../domain/colors';
import { messages } from '../../../../../i18n/messages';
import { useMembership } from '../../../../../customHooks/useMembership';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  deletionPeriod: {
    tableHead: {
      borderBottom: `2px solid ${TEAL200}`,
    },
    tableBody: {
      borderBottom: `1px solid ${GRAY300}`,
    },
  },
  overviewTab: {
    maxWidth: 530,
    withTwoButtons: {
      '@media (max-width: 420px)': {
        marginBottom: 52,
      },
    },
  },
  buttonAreaBasicButtonOne: {
    width: 200,
    margin: '0 8px',
    '@media (max-width: 420px)': {
      width: 300,
      display: 'block',
      margin: '0 auto 8px',
    },
  },
  buttonAreaBasicButtonTwo: {
    width: 200,
    margin: '0 8px',
    '@media (max-width: 420px)': {
      width: 300,
      display: 'block',
      margin: '8px auto 0',
    },
  },
};

interface OverviewTabProps {
  key: string;
  isBuyerPlus?: ContractSource;
}

const OverviewTab = ({ isBuyerPlus }: OverviewTabProps) => {
  return (
    <div className={`${css(styles.overviewTab, !isBuyerPlus && styles.overviewTab.withTwoButtons)}`}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.schufaCheck.modal.overview.paragraph'
          defaultMessage='Original SCHUFA-BonitätsCheck that...'
        />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg
            id='benefits.schufaCheck.modal.overview.bullet1'
            defaultMessage='Download online immediately - even...'
          />
        </li>
        <li>
          <Msg id='benefits.schufaCheck.modal.overview.bullet2' defaultMessage='Order once and...' />
        </li>
        <li>
          <Msg id='benefits.schufaCheck.modal.overview.bullet3' defaultMessage='Increase the chance of your...' />
        </li>
        <li>
          <Msg
            id='benefits.schufaCheck.modal.overview.bullet4'
            defaultMessage='Update monthly for free with KäuferPlus'
            values={{
              BuyerPlusLogo: <PlusBadge variant='buy' />,
            }}
          />
        </li>
      </ul>
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={PaperAndPenPng}
              imageWebp={PaperAndPenWebp}
              imageAlt={t({
                id: 'benefits.schufaCheck.modal.howItWorks.image1',
                defaultMessage: 'paper and pen',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheck.modal.howItWorks.paragraph1',
            defaultMessage: 'Enter your details',
          })}
        />
        <Item
          image={
            <ImageSource
              image={LetterPng}
              imageWebp={LetterWebp}
              imageAlt={t({
                id: 'benefits.schufaCheck.modal.howItWorks.image2',
                defaultMessage: 'opened letter',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheck.modal.howItWorks.paragraph2',
            defaultMessage: 'get your documents',
          })}
        />
        <Item
          omit={true}
          image={
            <ImageSource
              image={DownloadPng}
              imageWebp={DownloadWebp}
              imageAlt={t({
                id: 'benefits.schufaCheck.modal.howItWorks.image3',
                defaultMessage: 'download',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.schufaCheck.modal.howItWorks.paragraph3',
            defaultMessage: 'or directly download',
          })}
        />
        <Item
          image={
            <ImageSource
              image={CoinPng}
              imageWebp={CoinWebp}
              imageAlt={t({
                id: 'benefits.schufaCheck.modal.howItWorks.image4',
                defaultMessage: 'coin',
              })}
              sizes='100px'
            />
          }
          text={t(
            {
              id: 'benefits.schufaCheck.modal.howItWorks.paragraph4',
              defaultMessage: 'update monthly',
            },
            {
              BuyerPlusLogo: <PlusBadge variant='buy' />,
            },
          )}
        />
      </OrderedList>
    </div>
  );
};

const SchufaDeletionPeriodTable = () => {
  const deletionTableMessageKeys = Object.keys(messages['de-DE'].benefits.schufaCheck.modal.deletionPeriod);
  const additionalStyling = (index: number) => {
    if (index < 2) return `${css(styles.deletionPeriod.tableHead)}`;
    if (2 <= index && index < deletionTableMessageKeys.length - 2) return css(styles.deletionPeriod.tableBody);
    return '';
  };

  return (
    <div className='grid grid-flex' role='table'>
      {deletionTableMessageKeys.map((field, index) => {
        return (
          <div key={field} className={`grid-item one-half padding-m ${additionalStyling(index)}`}>
            <Msg id={`benefits.schufaCheck.modal.deletionPeriod.${field}`} defaultMessage={field} />
          </div>
        );
      })}
    </div>
  );
};

const Faq = () => {
  return (
    <div>
      <div className='font-heading-small-regular padding-bottom-l'>
        <Msg id='benefits.schufaCheck.modal.faqHeadline1' defaultMessage='SCHUFA-BonitätsCheck' />
      </div>
      <FAQList benefit='schufaCheck' start={0} end={8} />
      <div className='font-heading-small-regular padding-bottom-l'>
        <Msg id='benefits.schufaCheck.modal.faqHeadline2' defaultMessage='General Questions' />
      </div>
      <FAQList
        benefit='schufaCheck'
        start={9}
        end={14}
        values={{ SchufaDeletionPeriodTable: <SchufaDeletionPeriodTable /> }}
      />
      <CustomerService />
    </div>
  );
};

const buttonAreaPlus = (
  <div className='align-center'>
    <a
      className='button-secondary button-rounded'
      href={`${APP_CONFIG.schufaCheckUrl}?cmp_id=10-051666&cmp_name=solvency_check&cmp_position=residential_benefits_buy&cmp_creative=solvency_check_cta`}
    >
      <Msg id='benefits.schufaCheck.modal.plusLinkButton' defaultMessage='To the SCHUFA-BonitätsCheck' />
    </a>
  </div>
);

const buttonAreaBasic = (isMobileViewQueryParam: string) => {
  return (
    <div className='align-center'>
      <a
        className={`button-secondary button-rounded ${css(styles.buttonAreaBasicButtonOne)}`}
        href={`${APP_CONFIG.schufaCheckUrl}?cmp_id=10-051666&cmp_name=solvency_check&cmp_position=residential_benefits_buy&cmp_creative=solvency_check_cta`}
      >
        <Msg id='benefits.schufaCheck.modal.basicLinkButton1' defaultMessage='Create for 29,95€' />
      </a>
      <a
        className={`button-primary button-rounded ${css(styles.buttonAreaBasicButtonTwo)}`}
        href={`${APP_CONFIG.checkoutUrl}?cmp_id=10-051682&cmp_name=profile_buy_quickcheckout&cmp_position=residential_benefits_buy&cmp_creative=solvency_check_cta${isMobileViewQueryParam}`}
      >
        <Msg id='benefits.schufaCheck.modal.basicLinkButton2' defaultMessage='For free with plus' />
      </a>
    </div>
  );
};

export const SchufaCheck = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const { contractSource: isBuyerPlus } = useMembership();

  const [searchParams] = useSearchParams();
  const isMobileViewQueryParam =
    searchParams.get('headless') === 'true' || searchParams.get('isMobileView') === 'true' ? '&isMobileView=true' : '';

  return (
    <Modal
      title={t({ id: 'benefits.schufaCheck.headline', defaultMessage: 'SCHUFA-BonitätsCheck' })}
      isVisible={isVisible}
      image={SchufaCheckJpg}
      imageWebp={SchufaCheckWebp}
      imageMobile={SchufaCheckJpgMobile}
      imageMobileWebp={SchufaCheckWebpMobile}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.schufaCheck.modal.image',
        defaultMessage: 'SCHUFA=BonitätsCheck paper handover',
      })}
      tabsContent={[
        <OverviewTab key='overview' isBuyerPlus={isBuyerPlus} />,
        <HowItWorksTab key='howItWorks' />,
        <Faq key='faq' />,
      ]}
      buttonArea={isBuyerPlus ? buttonAreaPlus : buttonAreaBasic(isMobileViewQueryParam)}
      benefit={Benefit.SCHUFA_CHECK}
      onClose={onClose}
    />
  );
};
